import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  TextField,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Divider,
  Card,
  CardContent,
  IconButton,
  Alert
} from '@mui/material';
import {
  CreditCard as CreditCardIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Receipt as ReceiptIcon,
  Delete as DeleteIcon,
  Add as AddIcon
} from '@mui/icons-material';
import DashboardModal from '../../../../common/DashboardModal';
import DashboardButton from '../../../../common/DashboardButton';

const BillingDialog = ({
  open,
  onClose,
  currentBillingInfo = {},
  onSave
}) => {
  const { t } = useTranslation();
  
  // Form state
  const [billingData, setBillingData] = useState({
    companyName: '',
    businessAddress: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: ''
    },
    taxId: '',
    vatNumber: '',
    billingContact: {
      name: '',
      email: '',
      phone: ''
    },
    paymentMethods: [],
    subscription: {
      plan: 'basic',
      status: 'active',
      nextBilling: '',
      amount: 0
    }
  });

  const [newPaymentMethod, setNewPaymentMethod] = useState({
    type: 'credit',
    last4: '',
    expiryMonth: '',
    expiryYear: '',
    holderName: ''
  });

  const [showAddPayment, setShowAddPayment] = useState(false);
  const [errors, setErrors] = useState({});

  // Initialize form data
  useEffect(() => {
    if (open && currentBillingInfo) {
      setBillingData({
        companyName: currentBillingInfo.companyName || '',
        businessAddress: currentBillingInfo.businessAddress || {
          street: '',
          city: '',
          state: '',
          zipCode: '',
          country: ''
        },
        taxId: currentBillingInfo.taxId || '',
        vatNumber: currentBillingInfo.vatNumber || '',
        billingContact: currentBillingInfo.billingContact || {
          name: '',
          email: '',
          phone: ''
        },
        paymentMethods: currentBillingInfo.paymentMethods || [],
        subscription: currentBillingInfo.subscription || {
          plan: 'basic',
          status: 'active',
          nextBilling: '',
          amount: 0
        }
      });
    }
  }, [open, currentBillingInfo]);

  // Handle form field changes
  const handleInputChange = (field, value, section = null) => {
    setBillingData(prev => {
      if (section) {
        return {
          ...prev,
          [section]: {
            ...prev[section],
            [field]: value
          }
        };
      }
      return {
        ...prev,
        [field]: value
      };
    });
  };

  // Add payment method
  const handleAddPaymentMethod = () => {
    if (validatePaymentMethod()) {
      setBillingData(prev => ({
        ...prev,
        paymentMethods: [...prev.paymentMethods, { ...newPaymentMethod, id: Date.now() }]
      }));
      setNewPaymentMethod({
        type: 'credit',
        last4: '',
        expiryMonth: '',
        expiryYear: '',
        holderName: ''
      });
      setShowAddPayment(false);
    }
  };

  // Remove payment method
  const handleRemovePaymentMethod = (id) => {
    setBillingData(prev => ({
      ...prev,
      paymentMethods: prev.paymentMethods.filter(method => method.id !== id)
    }));
  };

  // Validate payment method
  const validatePaymentMethod = () => {
    const newErrors = {};
    if (!newPaymentMethod.last4 || newPaymentMethod.last4.length !== 4) {
      newErrors.last4 = t('billing.errors.invalidLast4');
    }
    if (!newPaymentMethod.holderName) {
      newErrors.holderName = t('billing.errors.holderNameRequired');
    }
    if (!newPaymentMethod.expiryMonth || !newPaymentMethod.expiryYear) {
      newErrors.expiry = t('billing.errors.expiryRequired');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    if (!billingData.companyName) {
      newErrors.companyName = t('billing.errors.companyNameRequired');
    }
    if (!billingData.billingContact.email) {
      newErrors.contactEmail = t('billing.errors.emailRequired');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle save
  const handleSave = () => {
    if (validateForm()) {
      onSave(billingData);
    }
  };

  // Get payment method display
  const getPaymentMethodDisplay = (method) => {
    const typeLabels = {
      credit: t('billing.paymentTypes.credit'),
      debit: t('billing.paymentTypes.debit'),
      paypal: t('billing.paymentTypes.paypal')
    };
    return `${typeLabels[method.type]} •••• ${method.last4}`;
  };

  return (
    <DashboardModal
      open={open}
      onClose={onClose}
      title={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ReceiptIcon />
          <Typography variant="h6">{t('billing.title')}</Typography>
        </Box>
      }
      description={t('billing.description')}
      size="lg"
      fullWidth
      actions={
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', width: '100%' }}>
          <DashboardButton
            variant="outlined"
            color="primary"
            onClick={onClose}
          >
            {t('common.cancel')}
          </DashboardButton>
          <DashboardButton
            variant="contained"
            color="primary"
            onClick={handleSave}
          >
            {t('common.saveChanges')}
          </DashboardButton>
        </Box>
      }
    >
      <Box sx={{ py: 2 }}>
        <Grid container spacing={4}>
          {/* Company Information */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <BusinessIcon color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t('billing.sections.company')}
              </Typography>
            </Box>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('billing.fields.companyName')}
                  value={billingData.companyName}
                  onChange={(e) => handleInputChange('companyName', e.target.value)}
                  error={!!errors.companyName}
                  helperText={errors.companyName}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('billing.fields.taxId')}
                  value={billingData.taxId}
                  onChange={(e) => handleInputChange('taxId', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('billing.fields.vatNumber')}
                  value={billingData.vatNumber}
                  onChange={(e) => handleInputChange('vatNumber', e.target.value)}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* Business Address */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <LocationIcon color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t('billing.sections.address')}
              </Typography>
            </Box>
            
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('billing.fields.street')}
                  value={billingData.businessAddress.street}
                  onChange={(e) => handleInputChange('street', e.target.value, 'businessAddress')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('billing.fields.city')}
                  value={billingData.businessAddress.city}
                  onChange={(e) => handleInputChange('city', e.target.value, 'businessAddress')}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t('billing.fields.state')}
                  value={billingData.businessAddress.state}
                  onChange={(e) => handleInputChange('state', e.target.value, 'businessAddress')}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t('billing.fields.zipCode')}
                  value={billingData.businessAddress.zipCode}
                  onChange={(e) => handleInputChange('zipCode', e.target.value, 'businessAddress')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('billing.fields.country')}
                  value={billingData.businessAddress.country}
                  onChange={(e) => handleInputChange('country', e.target.value, 'businessAddress')}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* Billing Contact */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              {t('billing.sections.contact')}
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={t('billing.fields.contactName')}
                  value={billingData.billingContact.name}
                  onChange={(e) => handleInputChange('name', e.target.value, 'billingContact')}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={t('billing.fields.contactEmail')}
                  type="email"
                  value={billingData.billingContact.email}
                  onChange={(e) => handleInputChange('email', e.target.value, 'billingContact')}
                  error={!!errors.contactEmail}
                  helperText={errors.contactEmail}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={t('billing.fields.contactPhone')}
                  value={billingData.billingContact.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value, 'billingContact')}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* Payment Methods */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CreditCardIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t('billing.sections.paymentMethods')}
                </Typography>
              </Box>
              <DashboardButton
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setShowAddPayment(true)}
              >
                {t('billing.actions.addPayment')}
              </DashboardButton>
            </Box>

            {/* Existing Payment Methods */}
            {billingData.paymentMethods.length > 0 && (
              <Box sx={{ mb: 3 }}>
                {billingData.paymentMethods.map((method) => (
                  <Card key={method.id} sx={{ mb: 2 }}>
                    <CardContent sx={{ py: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <CreditCardIcon color="primary" />
                          <Box>
                            <Typography variant="subtitle2">
                              {getPaymentMethodDisplay(method)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {method.holderName} • {t('billing.fields.expires')} {method.expiryMonth}/{method.expiryYear}
                            </Typography>
                          </Box>
                        </Box>
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => handleRemovePaymentMethod(method.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}

            {/* Add Payment Method Form */}
            {showAddPayment && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                    {t('billing.actions.addPayment')}
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>{t('billing.fields.paymentType')}</InputLabel>
                        <Select
                          value={newPaymentMethod.type}
                          onChange={(e) => setNewPaymentMethod(prev => ({ ...prev, type: e.target.value }))}
                        >
                          <MenuItem value="credit">{t('billing.paymentTypes.credit')}</MenuItem>
                          <MenuItem value="debit">{t('billing.paymentTypes.debit')}</MenuItem>
                          <MenuItem value="paypal">{t('billing.paymentTypes.paypal')}</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={t('billing.fields.holderName')}
                        value={newPaymentMethod.holderName}
                        onChange={(e) => setNewPaymentMethod(prev => ({ ...prev, holderName: e.target.value }))}
                        error={!!errors.holderName}
                        helperText={errors.holderName}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label={t('billing.fields.last4')}
                        value={newPaymentMethod.last4}
                        onChange={(e) => setNewPaymentMethod(prev => ({ ...prev, last4: e.target.value }))}
                        inputProps={{ maxLength: 4 }}
                        error={!!errors.last4}
                        helperText={errors.last4}
                      />
                    </Grid>
                    <Grid item xs={6} md={4}>
                      <TextField
                        fullWidth
                        label={t('billing.fields.expiryMonth')}
                        value={newPaymentMethod.expiryMonth}
                        onChange={(e) => setNewPaymentMethod(prev => ({ ...prev, expiryMonth: e.target.value }))}
                        inputProps={{ maxLength: 2 }}
                        error={!!errors.expiry}
                      />
                    </Grid>
                    <Grid item xs={6} md={4}>
                      <TextField
                        fullWidth
                        label={t('billing.fields.expiryYear')}
                        value={newPaymentMethod.expiryYear}
                        onChange={(e) => setNewPaymentMethod(prev => ({ ...prev, expiryYear: e.target.value }))}
                        inputProps={{ maxLength: 4 }}
                        error={!!errors.expiry}
                        helperText={errors.expiry}
                      />
                    </Grid>
                  </Grid>

                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
                    <DashboardButton
                      variant="outlined"
                      size="small"
                      onClick={() => setShowAddPayment(false)}
                    >
                      {t('common.cancel')}
                    </DashboardButton>
                    <DashboardButton
                      variant="contained"
                      size="small"
                      onClick={handleAddPaymentMethod}
                    >
                      {t('billing.actions.addPayment')}
                    </DashboardButton>
                  </Box>
                </CardContent>
              </Card>
            )}
          </Grid>

          {/* Subscription Info */}
          <Grid item xs={12}>
            <Divider />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              {t('billing.sections.subscription')}
            </Typography>
            
            <Alert severity="info" sx={{ mb: 3 }}>
              {t('billing.subscriptionInfo')}
            </Alert>

            <Grid container spacing={3}>
              <Grid item xs={12} md={3}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('billing.fields.currentPlan')}
                  </Typography>
                  <Typography variant="h6" sx={{ textTransform: 'capitalize' }}>
                    {billingData.subscription.plan}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('billing.fields.status')}
                  </Typography>
                  <Chip
                    label={billingData.subscription.status}
                    color={billingData.subscription.status === 'active' ? 'success' : 'default'}
                    size="small"
                    sx={{ textTransform: 'capitalize' }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('billing.fields.nextBilling')}
                  </Typography>
                  <Typography variant="body1">
                    {billingData.subscription.nextBilling || t('billing.notAvailable')}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('billing.fields.amount')}
                  </Typography>
                  <Typography variant="h6">
                    ${billingData.subscription.amount || 0}/month
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </DashboardModal>
  );
};

export default BillingDialog;